export interface CartLine {
  productId: string;
  colorId: string;
  sizeEu: string;
  quantity: number;
}

const STORAGE_KEY = 'aliyah_cart_v1';
export const CART_UPDATED_EVENT = 'cart:updated';
/** Dispatché uniquement sur un ajout (pas sur un changement de quantité/suppression) —
 * sert à déclencher l'ouverture automatique de la sidebar panier. */
export const CART_ITEM_ADDED_EVENT = 'cart:item-added';

function sameLine(a: CartLine, b: Pick<CartLine, 'productId' | 'colorId' | 'sizeEu'>): boolean {
  return a.productId === b.productId && a.colorId === b.colorId && a.sizeEu === b.sizeEu;
}

export function getCart(): CartLine[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function getCartCount(): number {
  return getCart().reduce((sum, line) => sum + line.quantity, 0);
}

function persist(lines: CartLine[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT, { detail: { count: lines.reduce((sum, l) => sum + l.quantity, 0) } }));
}

/** Ajoute une ligne au panier ; fusionne avec une ligne existante identique (même produit/couleur/taille). */
export function addToCart(line: CartLine, maxStock?: number): void {
  const lines = getCart();
  const existing = lines.find((l) => sameLine(l, line));

  if (existing) {
    existing.quantity += line.quantity;
    if (typeof maxStock === 'number') existing.quantity = Math.min(existing.quantity, maxStock);
  } else {
    const quantity = typeof maxStock === 'number' ? Math.min(line.quantity, maxStock) : line.quantity;
    lines.push({ ...line, quantity });
  }

  persist(lines);
  window.dispatchEvent(new CustomEvent(CART_ITEM_ADDED_EVENT));
}

export function updateQuantity(productId: string, colorId: string, sizeEu: string, quantity: number): void {
  const lines = getCart();
  const target = lines.find((l) => sameLine(l, { productId, colorId, sizeEu }));
  if (!target) return;

  if (quantity <= 0) {
    persist(lines.filter((l) => l !== target));
    return;
  }

  target.quantity = quantity;
  persist(lines);
}

export function removeLine(productId: string, colorId: string, sizeEu: string): void {
  const lines = getCart().filter((l) => !sameLine(l, { productId, colorId, sizeEu }));
  persist(lines);
}

export function clearCart(): void {
  persist([]);
}
